import { reactive } from "vue"
import TableConfiguration from "@/views/tables/TableConfiguration"
import { FilterDTO, PaginationOrder, PaginationParameter } from "@/model/bean/GeneratedDTOs"
import { SortOrder } from "@/model/SortOrder"
import Column from "@/views/tables/Column"
import TabParamsStore from "@/services/storage/TabParamsStore"
import RouterUtils from "@/router/RouterUtils"

export class BuefySortingPriority {
  field: string
  order: SortOrder
}

export interface TableViewOptions {
  tableRef?: string
  router: any
  route: any
  i18n: any
  getInitialColumns?: () => Array<Column>
  getDefaultSort?: () => Array<PaginationOrder>
  getInitialActiveFilters?: () => Array<FilterDTO>
  reloadData?: (pagination: PaginationParameter, filters: Array<FilterDTO>) => Promise<void>
  reloadDataWhenMounted?: boolean
  updateLocalStorageHistoryPagination?: () => void
}

export function useTableView(options: TableViewOptions): any {
  const state = reactive({
    tableRef: options.tableRef || "tableRef",
    isLoading: true,
    columns: new Array<Column>(),
    currentPagedSortedParameter: new PaginationParameter(),
    multipleSortEnabled: true,
    buefySortingPriority: new Array<BuefySortingPriority>(),
    total: 0,
    config: TableConfiguration.INSTANCE,
    perPage: TableConfiguration.INSTANCE.perPage,
    activeFilters: new Array<FilterDTO>(),
  })

  // Initialize table data
  function initialize() {
    initColumns()
    initFilters()
    initSorts()
  }

  // Initialize columns
  function initColumns() {
    // Load lastly used columns or used lastly stored version
    const lastlyFilteredColumns = TabParamsStore.INSTANCE.getLastVisibleColumns(
      options.router.currentRoute.path + state.tableRef,
    )

    if (lastlyFilteredColumns.length > 0) {
      state.columns = lastlyFilteredColumns
    } else if (options.getInitialColumns) {
      state.columns = options.getInitialColumns()
    }
  }

  // Initialize filters
  function initFilters() {
    // Load lastly used filters or used lastly stored version
    const lastlyActivatedFilters = TabParamsStore.INSTANCE.getLastFilters(
      options.router.currentRoute.path + state.tableRef,
    )

    const hasQueryFiltersInURL =
      options.route.query &&
      options.route.query.filters &&
      typeof options.route.query.filters === "string"

    if (lastlyActivatedFilters.length > 0 && !hasQueryFiltersInURL) {
      state.activeFilters = lastlyActivatedFilters
    } else if (options.getInitialActiveFilters) {
      state.activeFilters = options.getInitialActiveFilters()
    }
  }

  function setActiveFilters(newActiveFilters: Array<FilterDTO>) {
    state.activeFilters = newActiveFilters

    TabParamsStore.INSTANCE.storeFilters(
      options.router.currentRoute.path + state.tableRef,
      newActiveFilters,
    )
  }

  // Initialize sorts
  function initSorts() {
    // Load lastly used sorts or used lastly stored version
    const lastlyUsedSort = TabParamsStore.INSTANCE.getLastSorts(
      options.router.currentRoute.path + state.tableRef,
    )

    if (lastlyUsedSort.length > 0) {
      state.currentPagedSortedParameter.orderClauses = lastlyUsedSort
    } else if (options.getDefaultSort) {
      state.currentPagedSortedParameter.orderClauses = options.getDefaultSort()
    }
  }

  // Initialize mounted
  function initMounted() {
    state.currentPagedSortedParameter.pageNumber = 1
    state.currentPagedSortedParameter.pageSize = state.perPage
    refreshBuefySortingPriority()

    if (options.reloadDataWhenMounted !== false) {
      reload()
    }
  }

  // Refresh initial columns
  function refreshInitialColumns() {
    if (!options.getInitialColumns) return

    // Re-evaluate initial columns now that all data has been retrieved
    const refreshedInitialColumns = options.getInitialColumns()

    // But keep current visibility
    refreshedInitialColumns.forEach((refreshedColumn) => {
      const oldCorrespondingColumn = state.columns.filter(
        (col) => col.field == refreshedColumn.field,
      )

      if (oldCorrespondingColumn && oldCorrespondingColumn.length > 0) {
        refreshedColumn.visible = oldCorrespondingColumn[0].visible
      }
    })

    state.columns = refreshedInitialColumns
  }

  // Reload data
  async function reload() {
    if (options.reloadData) {
      state.isLoading = true
      try {
        await options.reloadData(state.currentPagedSortedParameter, state.activeFilters)
      } finally {
        state.isLoading = false
      }
    }
  }

  // Update visible columns
  function updateVisibleColumns(newVisibleColumnList: Array<Column>) {
    state.columns = newVisibleColumnList

    TabParamsStore.INSTANCE.storeVisibleColumns(
      options.router.currentRoute.path + state.tableRef,
      newVisibleColumnList,
    )
  }

  // Set previous pagination
  function setPreviousPagination(activePrefixUrls: string[], historyPaginationStorageKey: string) {
    // Si on arrive de la page du prefix alors on récupère la dernière pagination de la liste associée au prefix
    const previousRoute = RouterUtils.getPreviousRoutePath()
    const historyPagination = localStorage.getItem(historyPaginationStorageKey)
    const historyPaginationNumber = Number(historyPagination)

    if (!historyPagination || isNaN(historyPaginationNumber)) {
      return
    }

    const currentRoutePath = options.router.currentRoute.path
    const shouldRestoreFromPrevRoute =
      previousRoute.length > 0 && isActivePreviousRoute(activePrefixUrls, previousRoute)
    const shouldRestoreOnSameRoute = previousRoute === currentRoutePath
    const shouldRestoreWithoutHistory = previousRoute.length === 0

    if (shouldRestoreFromPrevRoute || shouldRestoreOnSameRoute || shouldRestoreWithoutHistory) {
      state.currentPagedSortedParameter.pageNumber = historyPaginationNumber
    }
  }

  // Check if previous route is active
  function isActivePreviousRoute(activePrefixUrls: string[], previousRoute: string): boolean {
    for (let i = 0; i < activePrefixUrls.length; i++) {
      if (previousRoute.startsWith(activePrefixUrls[i])) return true
    }
    return false
  }

  // Set local storage history pagination
  function setLocalStorageHistoryPagination(historyPaginationStorageKey: string) {
    localStorage.setItem(
      historyPaginationStorageKey,
      state.currentPagedSortedParameter?.pageNumber?.toString(),
    )
  }

  // Update local storage history pagination
  function updateLocalStorageHistoryPagination() {
    if (options.updateLocalStorageHistoryPagination) {
      options.updateLocalStorageHistoryPagination()
    }
  }

  function updateTotal(total: number): void {
    state.total = total
  }

  // On page change
  function onPageChange(newPage: number) {
    state.currentPagedSortedParameter.pageNumber = newPage
    updateLocalStorageHistoryPagination()
    reload()
  }

  // On sort
  function onSort(field: string, order: SortOrder) {
    if (!state.multipleSortEnabled) {
      // No multiple sorting, push a single sorting element
      const newSort = new PaginationOrder()
      newSort.clause = field
      newSort.desc = order == "desc"
      state.currentPagedSortedParameter.orderClauses = [newSort]
    } else {
      // Multiple sorting: search for existing sort to inverse its direction
      let foundOrderClause = false
      let i = 0

      while (!foundOrderClause && i < state.currentPagedSortedParameter.orderClauses.length) {
        const orderClause = state.currentPagedSortedParameter.orderClauses[i]

        if (orderClause.clause === field) {
          orderClause.desc = !orderClause.desc
          foundOrderClause = true
        }

        i++
      }

      if (!foundOrderClause) {
        const newSort = new PaginationOrder()
        newSort.clause = field
        newSort.desc = order == "desc"
        state.currentPagedSortedParameter.orderClauses.push(newSort)
      }
    }

    TabParamsStore.INSTANCE.storeSorts(
      options.router.currentRoute.path + state.tableRef,
      state.currentPagedSortedParameter.orderClauses,
    )

    refreshBuefySortingPriority()

    // Reset to page 1 when sorting
    state.currentPagedSortedParameter.pageNumber = 1

    reload()
  }

  // On sorting priority removed
  function sortingPriorityRemoved(field: string, tableRef: string = state.tableRef) {
    state.currentPagedSortedParameter.orderClauses =
      state.currentPagedSortedParameter.orderClauses.filter(
        (orderClause) => orderClause.clause !== field,
      )

    if (state.currentPagedSortedParameter.orderClauses.length === 0 && options.getDefaultSort) {
      state.currentPagedSortedParameter.orderClauses = options.getDefaultSort()

      // Cannot cast this as a B-Table, but it is the only possible type
      // @ts-ignore
      options.vueRefs[tableRef]?.resetMultiSorting()
    }

    TabParamsStore.INSTANCE.storeSorts(
      options.router.currentRoute.path + state.tableRef,
      state.currentPagedSortedParameter.orderClauses,
    )

    refreshBuefySortingPriority()
    reload()
  }

  // Refresh Buefy sorting priority
  function refreshBuefySortingPriority() {
    state.buefySortingPriority = state.currentPagedSortedParameter.orderClauses.map(
      (orderClause) => {
        const sortingPriority = new BuefySortingPriority()
        sortingPriority.field = orderClause.clause
        sortingPriority.order = orderClause.desc ? "desc" : "asc"
        return sortingPriority
      },
    )
  }

  // Update active filters
  function updateActiveFilters(newActiveFilters: Array<FilterDTO>) {
    setActiveFilters(newActiveFilters)
    reload()
  }

  // Initialize
  initialize()

  return {
    // State
    state,

    // Methods
    initialize,
    initMounted,
    refreshInitialColumns,
    reload,
    updateVisibleColumns,
    setPreviousPagination,
    isActivePreviousRoute,
    setLocalStorageHistoryPagination,
    updateLocalStorageHistoryPagination,
    onPageChange,
    onSort,
    sortingPriorityRemoved,
    refreshBuefySortingPriority,
    setActiveFilters,
    updateActiveFilters,
    updateTotal,
  }
}
